import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchContactInfo, submitFeedback, resetFeedbackStatus } from '../../store/slices/Contact/Index';
import { MapPin, Phone, Headphones, Mail, Globe, Clock, Facebook, Instagram, Twitter, Linkedin, Copy, Check } from 'lucide-react';
import Swal from 'sweetalert2';

interface FormData {
  name: string;
  phone: string;
  email: string;
  message: string;
}

const ContactUs: React.FC = () => {
  const dispatch = useAppDispatch();
  const { contactInfo, loading, feedbackStatus } = useAppSelector((state) => state.contact);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [currentLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'az';
  });

  const translations = {
    az: {
      title: "Bizimlə əlaqə",
      subtitle: "Bizimlə əlaqə saxlamaq üçün aşağıdakı formu doldurun",
      coordinates: "Koordinatlar",
      address: "Ünvan",
      addressText: "Azərbaycan, Bakı şəh., AZ1052, Ə.Rəcəbli küç. 19A",
      phone: "Telefon",
      customerService: "Müştəri xidmətləri",
      email: "Email",
      website: "Veb sayt",
      workingHours: "İş saatları",
      defaultWorkHours: "Bazar ertəsi - Cümə: 09:00 - 18:00",
      followUs: "Sosial mediada bizi izləyin",
      contactForm: "Bizimlə əlaqə formu",
      feedbackImportant: "Sizin fikirləriniz bizim üçün vacibdir!",
      feedbackDescription: "Bizə sual, rəylərinizi və ya şikayətlərinizi bildirə bilərsiniz.",
      namePlaceholder: "Ad və Soyad",
      phonePlaceholder: "Telefon nömrəsi",
      emailPlaceholder: "Email ünvanınızı daxil edin",
      messagePlaceholder: "Təklif və ya şikayəti bildirin...",
      submit: "Göndər",
      sending: "Göndərilir...",
      loading: "Məlumatlar yüklənir",
      successTitle: "Uğurlu!",
      successMessage: "Mesajınız uğurla göndərildi!",
      errorTitle: "Xəta!",
      warningTitle: "Diqqət!",
      fillAllFields: "Zəhmət olmasa bütün xanaları doldurun",
      close: "Bağla",
      copied: "Kopyalandı!"

    },
    ru: {
      title: "Связаться с нами",
      subtitle: "Заполните форму ниже, чтобы связаться с нами",
      coordinates: "Координаты",
      address: "Адрес",
      addressText: "Азербайджан, г. Баку, AZ1052, ул. А.Раджабли 19A",
      phone: "Телефон",
      customerService: "Служба поддержки",
      email: "Email",
      website: "Веб-сайт",
      workingHours: "Часы работы",
      defaultWorkHours: "Понедельник - Пятница: 09:00 - 18:00",
      followUs: "Следите за нами в социальных сетях",
      contactForm: "Форма обратной связи",
      feedbackImportant: "Ваше мнение важно для нас!",
      feedbackDescription: "Вы можете задать нам вопросы, оставить отзывы или жалобы.",
      namePlaceholder: "Имя Фамилия",
      phonePlaceholder: "Номер телефона",
      emailPlaceholder: "Введите ваш email",
      messagePlaceholder: "Напишите предложение или жалобу...",
      submit: "Отправить",
      sending: "Отправка...",
      loading: "Загрузка данных",
      successTitle: "Успешно!",
      successMessage: "Ваше сообщение успешно отправлено!",
      errorTitle: "Ошибка!",
      warningTitle: "Внимание!",
      fillAllFields: "Пожалуйста, заполните все поля",
      close: "Закрыть",
      copied: "Скопировано!"

    },
    en: {
      title: "Contact Us",
      subtitle: "Fill out the form below to contact us",
      coordinates: "Coordinates",
      address: "Address",
      addressText: "Azerbaijan, Baku city, AZ1052, A.Rajabli str. 19A",
      phone: "Phone",
      customerService: "Customer Service",
      email: "Email",
      website: "Website",
      workingHours: "Working Hours",
      defaultWorkHours: "Monday - Friday: 09:00 - 18:00",
      followUs: "Follow us on social media",
      contactForm: "Contact Form",
      feedbackImportant: "Your feedback is important to us!",
      feedbackDescription: "You can ask us questions, leave reviews or complaints.",
      namePlaceholder: "Full Name",
      phonePlaceholder: "Phone Number",
      emailPlaceholder: "Enter your email",
      messagePlaceholder: "Write a suggestion or complaint...",
      submit: "Submit",
      sending: "Sending...",
      loading: "Loading data",
      successTitle: "Success!",
      successMessage: "Your message has been sent successfully!",
      errorTitle: "Error!",
      warningTitle: "Warning!",
      fillAllFields: "Please fill in all fields",
      close: "Close",
      copied: "Copied!"

    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  useEffect(() => {
    dispatch(fetchContactInfo());
  }, [dispatch]);

  useEffect(() => {
    if (feedbackStatus.success) {
      Swal.fire({
        icon: 'success',
        title: t.successTitle,
        text: t.successMessage,
        confirmButtonText: t.close,
        confirmButtonColor: '#db2777'
      });
      setFormData({ name: '', phone: '', email: '', message: '' });
      dispatch(resetFeedbackStatus());
    }
    if (feedbackStatus.error) {
      Swal.fire({
        icon: 'error',
        title: t.errorTitle,
        text: feedbackStatus.error,
        confirmButtonText: t.close,
        confirmButtonColor: '#db2777'
      });
    }
  }, [feedbackStatus.success, feedbackStatus.error, dispatch, t]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.email || !formData.message) {
      Swal.fire({
        icon: 'warning',
        title: t.warningTitle,
        text: t.fillAllFields,
        confirmButtonText: t.close,
        confirmButtonColor: '#db2777'
      });
      return;
    }

    dispatch(submitFeedback({
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      feedback: formData.message
    }));
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  if (loading) {
    return (
      <div className=" flex  justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mx-auto">
        <h1 className="text-3xl font-semibold text-stone-800 ">{t.title}</h1>
        <p className="text-stone-600 leading-relaxed text-base mb-6 tracking-wide">
          {t.subtitle}
        </p>

        <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t.coordinates}</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Address */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 ">{t.address}</h3>
                <p className="text-gray-600">{t.addressText}</p>
              </div>
            </div>

            {/* Company Phone */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="w-6 h-6 text-pink-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{t.phone}</h3>
                <div className="flex items-center gap-2">
                  <a
                    href={`tel:${contactInfo?.company_phone}`}
                    className="text-gray-600 hover:text-pink-600 hover:underline cursor-pointer transition-colors"
                  >
                    {contactInfo?.company_phone}
                  </a>
                  <button
                    onClick={() => handleCopy(contactInfo?.company_phone || '', 'company_phone')}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title={t.copied}
                  >
                    {copiedField === 'company_phone' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Customer Service */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Headphones className="w-6 h-6 text-pink-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{t.customerService}</h3>
                <div className="flex items-center gap-2">
                  <a
                    href={`tel:${contactInfo?.customer_service_phone || '+994555557590'}`}
                    className="text-gray-600 hover:text-pink-600 hover:underline cursor-pointer transition-colors"
                  >
                    {contactInfo?.customer_service_phone || '+994 55 555 75 90'}
                  </a>
                  <button
                    onClick={() => handleCopy(contactInfo?.customer_service_phone || '+994 55 555 75 90', 'customer_service')}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title={t.copied}
                  >
                    {copiedField === 'customer_service' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-pink-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{t.email}</h3>
                <div className="flex items-center gap-2">
                  <a
                    href={`mailto:${contactInfo?.email || 'info@cabinet.az'}`}
                    className="text-gray-600 hover:text-pink-600 hover:underline cursor-pointer transition-colors"
                  >
                    {contactInfo?.email || 'info@cabinet.az'}
                  </a>
                  <button
                    onClick={() => handleCopy(contactInfo?.email || 'info@cabinet.az', 'email')}
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    title={t.copied}
                  >
                    {copiedField === 'email' ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            {/* Website */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t.website}</h3>
                <a
                  href="https://meqasigorta.az/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-600 hover:underline cursor-pointer"
                >
                  www.meqasigorta.az
                </a>
              </div>
            </div>

            {/* Working Hours */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-pink-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t.workingHours}</h3>
                <p className="text-gray-600">
                  {contactInfo?.work_hours
                    ? stripHtml(contactInfo.work_hours)
                    : t.defaultWorkHours}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        {contactInfo && (contactInfo.facebook_url || contactInfo.instagram_url || contactInfo.twitter_url || contactInfo.linkedin_url) && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">{t.followUs}</h2>
            <div className="flex gap-4">
              {contactInfo.facebook_url && (
                <a
                  href={contactInfo.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center justify-center transition-colors"
                >
                  <Facebook className="w-6 h-6 text-white" />
                </a>
              )}
              {contactInfo.instagram_url && (
                <a
                  href={contactInfo.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 rounded-lg flex items-center justify-center transition-colors"
                >
                  <Instagram className="w-6 h-6 text-white" />
                </a>
              )}
              {contactInfo.twitter_url && (
                <a
                  href={contactInfo.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-400 hover:bg-blue-500 rounded-lg flex items-center justify-center transition-colors"
                >
                  <Twitter className="w-6 h-6 text-white" />
                </a>
              )}
              {contactInfo.linkedin_url && (
                <a
                  href={contactInfo.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-700 hover:bg-blue-800 rounded-lg flex items-center justify-center transition-colors"
                >
                  <Linkedin className="w-6 h-6 text-white" />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Contact Form */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t.contactForm}</h2>

          <div className="flex items-start gap-4 mb-6">
            <div className="flex-1">
              <p className="text-gray-700 font-medium mb-1">{t.feedbackImportant}</p>
              <p className="text-sm text-gray-600">
                {t.feedbackDescription}
              </p>
            </div>
            <div className="text-4xl">📬</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t.namePlaceholder}
                className="flex-1 px-4 py-3 bg-gray-100 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-900"
                disabled={feedbackStatus.loading}
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  let value = e.target.value;

                  if (value === "") {
                    setFormData(prev => ({ ...prev, phone: "+994 " }));
                    return;
                  }

                  if (!value.startsWith("+994 ")) {
                    setFormData(prev => ({ ...prev, phone: "+994 " }));
                    return;
                  }

                  const digitsOnly = value.slice(5).replace(/\D/g, '');

                  if (digitsOnly.length <= 9) {
                    setFormData(prev => ({ ...prev, phone: `+994 ${digitsOnly}` }));
                  }
                }}
                onFocus={(e) => {
                  if (!formData.phone || formData.phone === "") {
                    setFormData(prev => ({ ...prev, phone: "+994 " }));
                    setTimeout(() => {
                      e.target.setSelectionRange(5, 5);
                    }, 0);
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const pastedText = e.clipboardData.getData('text');
                  const digits = pastedText.replace(/\D/g, '');

                  if (digits.length >= 9) {
                    const last9Digits = digits.slice(-9);
                    setFormData(prev => ({ ...prev, phone: `+994 ${last9Digits}` }));
                  } else if (digits.length > 0) {
                    const currentDigits = formData.phone.slice(5).replace(/\D/g, '');
                    const newDigits = (currentDigits + digits).slice(0, 9);
                    setFormData(prev => ({ ...prev, phone: `+994 ${newDigits}` }));
                  }
                }}
                placeholder={t.phonePlaceholder}
                className="flex-1 px-4 py-3 bg-gray-100 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-900"
                disabled={feedbackStatus.loading}
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t.emailPlaceholder}
                className="md:flex-[2] flex-1 px-4 py-3 bg-gray-100 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-900"
                disabled={feedbackStatus.loading}
              />
            </div>

            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder={t.messagePlaceholder}
              rows={5}
              className="w-full px-4 py-3 bg-gray-100 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-900 resize-none"
              disabled={feedbackStatus.loading}
            />

            <button
              type="submit"
              disabled={feedbackStatus.loading}
              className="w-full md:w-auto md:min-w-[200px] bg-colormainbrown hover:bg-colorbrownhover text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 mt-6 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {feedbackStatus.loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>{t.sending}</span>
                </>
              ) : (
                t.submit
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;